import React, { useEffect, useState } from 'react';
import './AdminDeposit.css';
import { db } from "../firebase";
import { ref, onValue, update, remove, get, set, push } from "firebase/database";
import { Plus, Check, X, History, Coins, Save, Settings2 } from 'lucide-react';
import Swal from 'sweetalert2';

const AdminDeposit = () => {
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [view, setView] = useState('pending');
  const [showModal, setShowModal] = useState(false);

  // Single Gateway State: ONLY USDT_TRC20
  const [gatewayForm, setGatewayForm] = useState({
    address: '',
    qrUrl: ''
  });

  useEffect(() => {
    // Fetch Only USDT TRC20 Data
    const gatewayRef = ref(db, 'adminSettings/payments/USDT_TRC20');
    onValue(gatewayRef, (snap) => {
      if (snap.exists()) {
        setGatewayForm(snap.val());
      }
    });

    // Pending Requests
    onValue(ref(db, 'depositRequests'), (snap) => {
      const data = snap.val();
      setRequests(data ? Object.keys(data).map(k => ({ id: k, ...data[k] })).reverse() : []);
    });

    // History
    onValue(ref(db, 'depositHistory'), (snap) => {
      const data = snap.val();
      setHistory(data ? Object.keys(data).map(k => ({ id: k, ...data[k] })).reverse() : []);
    });
  }, []);

  const handleInputChange = (field, value) => {
    setGatewayForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateGateway = async () => {
    try {
      // Save Only USDT TRC20 to DB
      await set(ref(db, 'adminSettings/payments/USDT_TRC20'), {
        address: gatewayForm.address,
        qrUrl: gatewayForm.qrUrl,
        lastUpdate: new Date().toISOString()
      });
      
      setShowModal(false);
      Swal.fire({
        title: 'Success!',
        text: 'USDT TRC20 Gateway Updated.',
        icon: 'success',
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#f3ba2f'
      });
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handleAction = async (req, type) => {
    try {
      const finalStatus = type === 'approve' ? 'Success' : 'Rejected';

      if (type === 'approve') {
        const uRef = ref(db, `users/${req.username}`);
        const uSnap = await get(uRef);
        if (uSnap.exists()) {
          const currentBalance = parseFloat(uSnap.val().balance || 0);
          await update(uRef, { balance: (currentBalance + parseFloat(req.amount)).toFixed(2) });
        }
      }

      await push(ref(db, 'depositHistory'), { 
        ...req, 
        status: finalStatus, 
        processedAt: new Date().toISOString() 
      });

      await remove(ref(db, `depositRequests/${req.id}`));

      Swal.fire({
        title: type === 'approve' ? 'Approved!' : 'Rejected!',
        icon: 'success',
        timer: 1500,
        background: '#1a1a1a',
        color: '#fff',
        showConfirmButton: false
      });

    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  return (
    <div className="admin-compact-container">
      <div className="mini-header">
        <div className="luxury-admin-header">
          <div className="header-title-section">
            <Settings2 size={24} color="#f3ba2f" />
            <h2>Deposit Management</h2>
          </div>
          <button onClick={() => setShowModal(true)} className="manage-gateways-btn">
            <Plus size={18} /> Manage TRC20 Gateway
          </button>
        </div>
        
        <div className="nav-tabs">
          <button className={view === 'pending' ? 'active' : ''} onClick={() => setView('pending')}>
            Pending ({requests.length})
          </button>
          <button className={view === 'history' ? 'active' : ''} onClick={() => setView('history')}>
            <History size={14}/> History
          </button>
        </div>
      </div>

      <div className="compact-table-card">
        <table className="slim-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Asset</th>
              <th>Amount</th>
              <th>Time</th>
              <th>{view === 'pending' ? 'Control' : 'Status'}</th>
            </tr>
          </thead>
          <tbody>
            {(view === 'pending' ? requests : history).map((item) => (
              <tr key={item.id} className="slim-row">
                <td className="user-td"><b>{item.username}</b></td>
                <td><span className="status-tag success">TRC20</span></td>
                <td className="amt-td">${parseFloat(item.amount).toFixed(2)}</td>
                <td className="time-td">{new Date(item.timestamp || item.processedAt).toLocaleString()}</td>
                <td>
                  {view === 'pending' ? (
                    <div className="slim-actions">
                      <button className="check-btn" onClick={() => handleAction(item, 'approve')} style={{background:'#0ecb81', border:'none', borderRadius:'5px', padding:'5px', cursor:'pointer', marginRight:'5px'}}><Check size={18} color="#fff"/></button>
                      <button className="x-btn" onClick={() => handleAction(item, 'reject')} style={{background:'#ff4d4d', border:'none', borderRadius:'5px', padding:'5px', cursor:'pointer'}}><X size={18} color="#fff"/></button>
                    </div>
                  ) : (
                    <span className={`status-tag ${item.status.toLowerCase()}`}>{item.status.toUpperCase()}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(view === 'pending' ? requests : history).length === 0 && <div className="no-data">No records found.</div>}
      </div>

      {showModal && (
        <div className="bulk-modal-overlay">
          <div className="bulk-modal">
            <div className="modal-head">
              <div className="head-content">
                <Coins size={22} color="#f3ba2f" />
                <h4>Gateway Settings</h4>
              </div>
              <X size={24} style={{cursor:'pointer', color:'#888'}} onClick={() => setShowModal(false)} />
            </div>
            
            <div className="modal-scroll-body">
              <p className="modal-sub">Set your USDT TRC20 deposit address and QR code.</p>
              
              <div className="coin-update-row">
                <div className="coin-label-brand">
                  <img src="https://cryptologos.cc/logos/tether-usdt-logo.png" alt="USDT" />
                  <span>USDT TRC20</span>
                </div>
                
                <div className="input-box">
                  <label>Wallet Address</label>
                  <input 
                    type="text" 
                    value={gatewayForm.address} 
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Enter TRC20 Address"
                  />
                </div>
                
                <div className="input-box">
                  <label>QR Code URL (Imgur/Direct Link)</label>
                  <input 
                    type="text" 
                    value={gatewayForm.qrUrl} 
                    onChange={(e) => handleInputChange('qrUrl', e.target.value)}
                    placeholder="https://i.imgur.com/example.png"
                  />
                </div>
              </div>
            </div>

            <div className="modal-foot">
              <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="save-bulk-btn" onClick={handleUpdateGateway}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeposit;